import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { SubscribeModal } from "../components/SubscribeModal";
import { RequirementsList } from "../components/RequirementsList";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Bookmark, BookmarkCheck, Sparkles, ExternalLink, Coins, Clock, ArrowLeft, Loader2, Building2,
} from "lucide-react";
import { toast } from "sonner";

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPaid } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);

  const load = () => {
    api.get(`/campaign/${id}`).then((r) => setC(r.data)).catch(() => toast.error("Campaign not found")).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const toggleSave = async () => {
    try {
      if (c.is_saved) await api.delete(`/campaign/${c.id}/save`);
      else await api.post(`/campaign/${c.id}/save`);
      setC({ ...c, is_saved: !c.is_saved });
      toast.success(c.is_saved ? "Removed from saved" : "Campaign saved");
    } catch {
      toast.error("Action failed");
    }
  };

  const startClipping = () => {
    if (isPaid) navigate(`/workspace?campaign=${c.id}`);
    else setModal(true);
  };

  if (loading) return <div className="min-h-screen grain"><Navbar /><div className="max-w-4xl mx-auto px-6 py-20"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div></div>;
  if (!c) return <div className="min-h-screen grain"><Navbar /><div className="max-w-4xl mx-auto px-6 py-20 text-zinc-500">Campaign not found.</div></div>;

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <SubscribeModal open={modal} onOpenChange={setModal} />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <button onClick={() => navigate("/campaigns")} data-testid="back-btn" className="flex items-center gap-1.5 text-zinc-500 hover:text-white text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to campaigns
        </button>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#141419] border border-white/10 rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Badge className="bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-mono text-[10px] uppercase tracking-wider">{c.platform_name}</Badge>
                <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px]">{c.status}</Badge>
                <Badge className="bg-white/5 text-zinc-300 border border-white/10 text-[10px]">{c.category}</Badge>
              </div>
              <h1 className="font-display text-3xl font-bold mb-2">{c.name}</h1>
              {c.brand && <p className="text-zinc-400 flex items-center gap-1.5 mb-4"><Building2 className="w-4 h-4" /> {c.brand}</p>}
              <div className="flex flex-wrap gap-5 text-sm">
                {c.payout && <span className="flex items-center gap-1.5 text-emerald-400"><Coins className="w-4 h-4" /> {c.payout}</span>}
                {c.deadline && <span className="flex items-center gap-1.5 text-zinc-300"><Clock className="w-4 h-4" /> Deadline {c.deadline}</span>}
                {c.external_url && (
                  <a href={c.external_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-violet-400 hover:text-violet-300" data-testid="external-url-link">
                    <ExternalLink className="w-4 h-4" /> View source
                  </a>
                )}
              </div>
              {c.required_platforms?.length > 0 && (
                <div className="mt-5 pt-5 border-t border-white/5">
                  <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">Required platforms</p>
                  <div className="flex flex-wrap gap-2">
                    {c.required_platforms.map((p) => (
                      <span key={p} className="px-2.5 py-1 rounded-lg bg-white/5 text-xs text-zinc-300">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Requirements */}
            <div className="bg-[#141419] border border-amber-500/20 rounded-2xl p-8">
              <h2 className="font-display text-xl font-bold mb-1">Campaign Requirements</h2>
              <p className="text-zinc-500 text-sm mb-5">Clipstar will enforce these automatically when generating clips.</p>
              <RequirementsList requirements={c.requirements} />
            </div>

            {c.rules_text && (
              <div className="bg-[#141419] border border-white/10 rounded-2xl p-8">
                <h2 className="font-display text-lg font-bold mb-3">Campaign Rules</h2>
                <p className="text-sm text-zinc-400 leading-relaxed whitespace-pre-wrap">{c.rules_text}</p>
              </div>
            )}
          </div>

          {/* actions */}
          <div className="space-y-4">
            <div className="bg-[#141419] border border-white/10 rounded-2xl p-6 sticky top-24">
              <Button
                onClick={toggleSave}
                data-testid="save-campaign-btn"
                variant="outline"
                className="w-full border-white/10 hover:bg-white/5 mb-3 h-11"
              >
                {c.is_saved ? <><BookmarkCheck className="w-4 h-4 mr-2 text-amber-400" /> Saved</> : <><Bookmark className="w-4 h-4 mr-2" /> Save Campaign</>}
              </Button>
              <Button
                onClick={startClipping}
                data-testid="start-clipping-btn"
                className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold h-11 amber-glow"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Start Clipping
              </Button>
              {!isPaid && (
                <p className="text-xs text-zinc-500 text-center mt-3">
                  Viewing & saving is free. Clipping needs a subscription.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
